import React from "react";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const SnapshotVerification = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="7" 
        title="Snapshot Verification" 
        description="Snapshot Verification is the formal technical handshake between your team and the CTB researcher pod. Once verified, the scope is immutable and forms the basis of the Pentest Report."
      />


      <h2 id="workflow" className="text-xl font-bold text-black mt-10 mb-4">7.1 The Technical Handshake</h2>
      <p className="mb-6 text-slate-600 leading-relaxed">Snapshot verification is a formal sign-off. The platform transitions through three deterministic states for every testing scope.</p>

      <div className="space-y-4 mb-12">
        {[
          { state: "Draft Scope", color: "bg-amber-100 text-amber-700", desc: "Scope is fully mutable. You can add targets, remove endpoints, or use the 'Pull Inventory' action to sync from the global asset database." },
          { state: "Pending Verification", color: "bg-blue-100 text-blue-700", desc: "Submitted to CTB Admin. The scope is technically locked while our lead researcher performs a feasibility audit and safety review." },
          { state: "Scope Verified", color: "bg-green-100 text-green-700", desc: "Sign-off complete. Researchers are assigned tokens, and the scope becomes technically immutable for the remainder of the run." },
        ].map(item => (
          <div key={item.state} className="flex gap-4 p-4 rounded-xl border border-[#eaeaea] bg-white">
            <div className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase h-fit ${item.color} shrink-0`}>
              {item.state}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed m-0">{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 id="guidance" className="text-xl font-bold text-black mt-12 mb-4">7.2 Automated Guidance System</h2>
      <p className="mb-6 text-slate-600 leading-relaxed">To ensure testing integrity, the platform utilizes an <b>Automated Guidance Nudge</b>. When a run is scheduled but scope is unverified, a persistent notification will guide you directly to the Review modal.</p>
      
      <div className="rounded-2xl border-l-4 border-emerald-400 bg-emerald-50/50 p-6 my-8">
        <h4 className="font-bold text-emerald-900 flex items-center gap-2 mb-2">
          <span>🛡️</span> Compliance & Integrity
        </h4>
        <p className="text-xs text-emerald-800/80 leading-relaxed m-0">
          <b>Why Immutability?</b> Once a run starts, the scope is locked. This ensures that the generated Pentest Report reflects a precise point-in-time snapshot, fulfilling the strict audit requirements for <b>SOC2 Type II</b> and <b>ISO 27001</b>.
        </p>
      </div>

      <div className="mt-8 p-6 rounded-lg border border-[#eaeaea] bg-[#fafafa]">
        <span className="font-bold text-black">Pull Inventory Action:</span> While in Draft, use this command to instantly synchronize newly discovered endpoints or targets from your Global Asset Inventory into the active testing run.
      </div>

      <footer className="mt-24 border-t border-[#eaeaea] py-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-sm text-slate-500 font-medium">
            Capture The Bug  ·  Enterprise Onboarding Guide  ·  Version 1.0  ·  2026
          </div>
          <div className="text-sm text-slate-500">
            For support: <a href="mailto:support@capturethebug.xyz" className="text-[#0070f3] hover:underline">support@capturethebug.xyz</a>
          </div>
        </div>
      </footer>
    </SearchHighlighter>
  );
};

export default SnapshotVerification;
